import { __assign } from "tslib";
import React, { useRef, useState } from 'react';
import { Key } from 'ts-key-enum';
import { Box } from '../Box';
import { ClickAwayListener } from '../ClickAwayListener';
import { Listbox } from '../Listbox';
import { ListboxOption } from '../Listbox/components';
import { CircularProgress } from '../Progress';
import { useStyles } from './ListboxDropdown.styles';
import { Typography } from '../Typography';
import { useListboxHandlers } from '../Listbox/useListboxHandlers';
import { FloatingDropdown, InlineDropdown } from './components';
var ERROR_OPTION = { id: 'error' };
var DEFAULT_OPTION = { id: 'default' };
export var ListboxDropdown = function (props) {
    var _a, _b;
    var id = props.id, strings = props.strings, renderInput = props.renderInput, _c = props.variant, variant = _c === void 0 ? 'floating' : _c, _d = props.options, options = _d === void 0 ? null : _d, open = props.open, _e = props.loading, loading = _e === void 0 ? false : _e, _f = props.error, error = _f === void 0 ? false : _f, _g = props.openOnFocus, openOnFocus = _g === void 0 ? false : _g, _h = props.closeOnSelect, closeOnSelect = _h === void 0 ? true : _h, _j = props.keepSelectedOption, keepSelectedOption = _j === void 0 ? true : _j, onOptionSelected = props.onOptionSelected, onDefaultOptionSelected = props.onDefaultOptionSelected, onErrorOptionSelected = props.onErrorOptionSelected, renderEmptyState = props.renderEmptyState, renderErrorState = props.renderErrorState, ClickAwayListenerProps = props.ClickAwayListenerProps, ContainerProps = props.ContainerProps, InputContainerProps = props.InputContainerProps, PopperProps = props.PopperProps, BoxProps = props.BoxProps, CircularProgressProps = props.CircularProgressProps, SubheaderProps = props.SubheaderProps, ListboxProps = props.ListboxProps, DefaultBoxProps = props.DefaultBoxProps;
    var _k = useState(null), anchorEl = _k[0], setAnchorEl = _k[1];
    var _l = React.useState(false), listboxHasFocus = _l[0], setListboxHasFocus = _l[1];
    var _m = useState(false), internalOpen = _m[0], setInternalOpen = _m[1];
    var containerRef = useRef(null);
    var _o = useState(''), focusedOptionId = _o[0], setFocusedOptionId = _o[1];
    var selectedOptionRef = useRef(null);
    var ownInputRef = useRef(null);
    var inputRef = (_a = props.inputRef) !== null && _a !== void 0 ? _a : ownInputRef;
    var Dropdown = variant === 'inline' ? InlineDropdown : FloatingDropdown;
    function setFirstOptionAsFocused() {
        if ((options === null || options === void 0 ? void 0 : options.length) && options.length > 0) {
            var firstOptionId = "".concat(id, "-option-0");
            setFocusedOptionId(firstOptionId);
        }
    }
    function openDropdown() {
        setInternalOpen(true);
        setListboxHasFocus(true);
        setFirstOptionAsFocused();
    }
    function closeDropdown() {
        setInternalOpen(false);
        setListboxHasFocus(false);
        setFocusedOptionId('');
        if (!keepSelectedOption) {
            selectedOptionRef.current = null;
        }
    }
    function isOptionSelected(option) {
        return selectedOptionRef.current === option;
    }
    function selectOption(option) {
        if (keepSelectedOption) {
            selectedOptionRef.current = option;
        }
        if (closeOnSelect) {
            closeDropdown();
        }
        if (option !== DEFAULT_OPTION && option !== ERROR_OPTION) {
            onOptionSelected === null || onOptionSelected === void 0 ? void 0 : onOptionSelected(option);
        }
        if (option === DEFAULT_OPTION) {
            onDefaultOptionSelected === null || onDefaultOptionSelected === void 0 ? void 0 : onDefaultOptionSelected();
        }
        if (option === ERROR_OPTION) {
            onErrorOptionSelected === null || onErrorOptionSelected === void 0 ? void 0 : onErrorOptionSelected();
        }
    }
    function deselectOption() {
        selectedOptionRef.current = null;
    }
    function updateFocusedOption(elem) {
        var elemId = elem === null || elem === void 0 ? void 0 : elem.getAttribute('id');
        if (elemId) {
            setFocusedOptionId(elemId);
        }
    }
    function isOptionFocused(optionId) {
        return optionId === focusedOptionId;
    }
    var _p = useListboxHandlers({
        hasFocus: listboxHasFocus,
        isOptionSelected: isOptionSelected,
        selectOption: selectOption,
        deselectOption: deselectOption,
        onEscape: function () {
            var _a;
            closeDropdown();
            (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        },
        onTab: function () {
            closeDropdown();
        },
        updateFocusedOption: updateFocusedOption,
    }), listRef = _p.listRef, handleOptionOnClick = _p.handleOnMouseUp, handleOptionOnKeyDown = _p.handleOnKeyDown;
    function handleListboxOptionOnKeyDown(event, option) {
        if (event.key === Key.Enter) {
            // Ignore enter because the MUI menu item click event handles this
            return;
        }
        handleOptionOnKeyDown(event, option);
    }
    function handleOnClickAway(event) {
        closeDropdown();
        ClickAwayListenerProps === null || ClickAwayListenerProps === void 0 ? void 0 : ClickAwayListenerProps.onClickAway(event);
    }
    function handleInputOnTextChange(text) {
        setInternalOpen(text !== '');
        setFocusedOptionId('');
        setListboxHasFocus(false);
    }
    function handleInputOnKeyDown(event) {
        switch (event.key) {
            case Key.ArrowDown:
                if (!loading) {
                    openDropdown();
                }
                event.preventDefault();
                break;
            case Key.Escape:
                if (internalOpen) {
                    closeDropdown();
                    event.preventDefault();
                }
                break;
            case Key.Tab:
                if (event.shiftKey) {
                    setInternalOpen(false);
                }
        }
    }
    function handleInputOnFocus() {
        if (openOnFocus) {
            setInternalOpen(true);
        }
        setFocusedOptionId('');
        setListboxHasFocus(false);
    }
    function handleInputOnBlur(event) {
        var _a;
        if (!listboxHasFocus &&
            event.relatedTarget instanceof Node &&
            !((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.relatedTarget))) {
            setFocusedOptionId('');
            setInternalOpen(false);
        }
    }
    var classes = useStyles();
    var isOpen = open || loading || internalOpen;
    var shouldRenderErrorState = !loading && error && !!renderErrorState;
    var shouldRenderEmptyState = !loading && !error && options && options.length === 0 && !!renderEmptyState;
    var shouldRenderOptions = !loading && !error && options && options.length > 0;
    var shouldRenderDefaultAction = shouldRenderOptions && !!strings.defaultActionText;
    var shouldRenderListbox = shouldRenderErrorState || shouldRenderOptions;
    var shouldRenderSubheader = shouldRenderOptions && !!(strings.subheaderText && ((_b = strings.subheaderText) === null || _b === void 0 ? void 0 : _b.length) > 0);
    var loadingId = "".concat(id, "-loading");
    var emptyId = "".concat(id, "-empty");
    var circularProgressId = "".concat(id, "-circular-progress");
    var subheaderId = "".concat(id, "-subheader");
    var listboxId = "".concat(id, "-listbox");
    var listBoxOptionId = "".concat(id, "-option-");
    var errorId = "".concat(listBoxOptionId).concat(ERROR_OPTION.id);
    var defaultOptionId = "".concat(listBoxOptionId).concat(DEFAULT_OPTION.id);
    var getAriaControls = function () {
        if (loading) {
            return loadingId;
        }
        if (shouldRenderEmptyState) {
            return emptyId;
        }
        return listboxId;
    };
    var internalRenderLoading = function () {
        if ('renderLoading' in props && typeof props.renderLoading === 'function') {
            return props.renderLoading();
        }
        return (React.createElement(CircularProgress, __assign({ id: circularProgressId, ariaLabel: strings.loadingAriaLabel, size: "medium" }, CircularProgressProps)));
    };
    var internalRenderOption = function (option, index) {
        var optionId = "".concat(listBoxOptionId).concat(index);
        var listboxOptionProps = {
            id: optionId,
            onClick: function () { return handleOptionOnClick(option); },
            onKeyDown: function (event) {
                return handleListboxOptionOnKeyDown(event, option);
            },
            selected: isOptionSelected(option),
            'aria-selected': isOptionFocused(optionId),
        };
        if ('renderOption' in props) {
            return (React.createElement(ListboxOption, __assign({ key: index }, listboxOptionProps, { className: classes.listboxOption }), props.renderOption(option)));
        }
        if ('renderCompleteOption' in props) {
            return props.renderCompleteOption(listboxOptionProps, option, index);
        }
        throw new TypeError('You should give either renderOption or renderCompleteOption');
    };
    return (React.createElement(ClickAwayListener, __assign({}, ClickAwayListenerProps, { onClickAway: handleOnClickAway }),
        React.createElement("div", __assign({}, ContainerProps, { ref: containerRef }),
            React.createElement("div", __assign({}, InputContainerProps, { ref: setAnchorEl }), renderInput({
                onTextChange: handleInputOnTextChange,
                onKeyDown: handleInputOnKeyDown,
                onFocus: handleInputOnFocus,
                onBlur: handleInputOnBlur,
                inputRef: inputRef,
                ariaProps: {
                    role: 'combobox',
                    'aria-autocomplete': 'list',
                    'aria-expanded': isOpen,
                    'aria-controls': getAriaControls(),
                    'aria-activedescendant': focusedOptionId,
                },
            })),
            React.createElement(Dropdown, { anchorEl: anchorEl, isOpen: isOpen, PopperProps: PopperProps, BoxProps: BoxProps },
                loading && (React.createElement(Box, { id: loadingId, className: classes.item }, internalRenderLoading())),
                shouldRenderEmptyState && (React.createElement(Box, { id: emptyId, className: classes.subheader }, renderEmptyState === null || renderEmptyState === void 0 ? void 0 : renderEmptyState())),
                shouldRenderSubheader && (React.createElement(Box, { className: classes.subheader },
                    React.createElement(Typography, __assign({ id: subheaderId, variant: "h6", component: "span" }, SubheaderProps), strings.subheaderText))),
                shouldRenderListbox && (React.createElement(Listbox, __assign({ ref: listRef, id: listboxId, "aria-labelledby": error ? errorId : subheaderId }, ListboxProps),
                    shouldRenderErrorState && (React.createElement(ListboxOption, { id: errorId, onClick: function () { return handleOptionOnClick(ERROR_OPTION); }, onKeyDown: function (event) {
                            return handleListboxOptionOnKeyDown(event, ERROR_OPTION);
                        }, selected: isOptionSelected(ERROR_OPTION), "aria-selected": isOptionFocused(errorId), autoFocus: true }, renderErrorState === null || renderErrorState === void 0 ? void 0 : renderErrorState())),
                    shouldRenderOptions && (React.createElement(React.Fragment, null, options === null || options === void 0 ? void 0 :
                        options.map(function (option, index) { return internalRenderOption(option, index); }),
                        shouldRenderDefaultAction && (React.createElement(ListboxOption, { id: defaultOptionId, onClick: function () { return handleOptionOnClick(DEFAULT_OPTION); }, onKeyDown: function (event) {
                                return handleListboxOptionOnKeyDown(event, DEFAULT_OPTION);
                            }, selected: isOptionSelected(DEFAULT_OPTION), "aria-selected": isOptionFocused(defaultOptionId) },
                            React.createElement(Box, __assign({}, DefaultBoxProps),
                                React.createElement(Typography, { className: classes.link }, strings.defaultActionText))))))))))));
};
export default ListboxDropdown;
//# sourceMappingURL=ListboxDropdown.js.map